import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'tea-component';
import { GptOptionsType, PromptType } from '@op/components/GPT/types';
import EditableModal from '@op/components/EditableModal';
import GPT from '@op/components/GPT';
import PromptSection from '../AISummarySetting/PromptSection';
import useLanguage from '@op/hooks/useLanguage';

/**
 * PromptSection 中会参与「草稿态」编辑、并在确定时同步回外部的字段
 */
export interface PromptEditDraft {
  prompt: string;
  gptOptions: GptOptionsType;
  activePromptId: string;
  errorText: string;
}

export interface PromptEditModalProps {
  /** 是否显示 */
  visible: boolean;
  /** 弹窗标题 */
  title?: React.ReactNode;
  /** 外部传入的初始数据，用于初始化草稿 */
  value: PromptEditDraft;
  /** 点击「确定」时同步草稿回外部 */
  onConfirm: (draft: PromptEditDraft) => void;
  /** 关闭弹窗（取消/关闭），不触发数据同步 */
  onClose: () => void;
  /** PromptSection 只读/回调相关配置 */
  realPromptList: PromptType[];
  defaultPromptList: any[];
  userPromptList: any[];
  onSavePrompt?: (promptName: string, promptText: string) => Promise<any>;
  onUpdatePrompt?: (
    id: string,
    promptName: string,
    promptText: string,
  ) => Promise<any>;
  onDeletePrompt?: (id: string) => Promise<any>;
  onApplyPrompt?: (id: string) => Promise<any>;
  /** 是否弹窗模式展示（隐藏提示编号箭头等） */
  isModal?: boolean;
  okText?: string;
  cancelText?: string;
  /** 获取历史 prompt 列表 */
  getUserHistoryPrompt?: () => void;
  /** 历史 prompt 加载中 */
  historyLoading?: boolean;
}

const PromptEditModal: React.FC<PromptEditModalProps> = ({
  visible,
  title,
  value,
  onConfirm,
  onClose,
  realPromptList,
  defaultPromptList,
  userPromptList,
  onSavePrompt,
  onUpdatePrompt,
  onDeletePrompt,
  onApplyPrompt,
  isModal = false,
  okText,
  cancelText,
  getUserHistoryPrompt,
  historyLoading = false,
}) => {
  // PromptSection 内部通过该 ref 记录用户是否手动编辑过 prompt
  const promptEdit = useRef(false);
  // 历史 prompt 弹窗状态
  const [modalVisible, setModalVisible] = useState(false);

  const togglePromptModal = () => {
    setModalVisible(!modalVisible);
  };

  return (
    <EditableModal
      visible={visible}
      title={title}
      value={value}
      onConfirm={onConfirm}
      onClose={onClose}
      okText={okText}
      size={1200}
      cancelText={cancelText}
      className="dbo-editable-modal"
    >
      {({ prompt, gptOptions, activePromptId, errorText }, setDraft) => (
        <>
          <div className="dbo-editable-modal-body">
            <PromptSection
              prompt={prompt}
              setPrompt={(nextPrompt) =>
                setDraft((d) => ({ ...d, prompt: nextPrompt }))
              }
              gptOptions={gptOptions}
              errorText={errorText}
              setErrorText={(nextErrorText) =>
                setDraft((d) => ({ ...d, errorText: nextErrorText }))
              }
              realPromptList={realPromptList}
              defaultPromptList={defaultPromptList}
              userPromptList={userPromptList}
              togglePromptModal={togglePromptModal}
              onSavePrompt={onSavePrompt}
              onUpdatePrompt={onUpdatePrompt}
              onDeletePrompt={onDeletePrompt}
              onApplyPrompt={onApplyPrompt}
              isModal={isModal}
              activePromptId={activePromptId}
              setActivePromptId={(nextActivePromptId) =>
                setDraft((d) => ({ ...d, activePromptId: nextActivePromptId }))
              }
              promptEdit={promptEdit}
              textareaHeight={520}
            />
          </div>

          {/* 历史 prompt 弹窗，与 AISummarySetting 保持一致 */}
          <PromptModal
            visible={modalVisible}
            toggleVisible={togglePromptModal}
            setPrompt={(nextPrompt: string) => {
              promptEdit.current = true;
              setDraft((d) => ({ ...d, prompt: nextPrompt }));
            }}
            userPromptList={userPromptList}
            loading={historyLoading}
            getUserHistoryPrompt={getUserHistoryPrompt}
          />
        </>
      )}
    </EditableModal>
  );
};

const PromptModal = (props: any) => {
  const {
    visible,
    toggleVisible,
    setPrompt,
    userPromptList,
    loading,
    getUserHistoryPrompt,
  } = props;
  const { formatMessage } = useLanguage();

  const getCaptions = useMemo(() => {
    return (
      <div className="dbo-ai-summary-setting-modal-title">
        {formatMessage({ id: 'Historical prompt' })}
        <span className="dbo-ai-summary-setting-modal-tips">
          {formatMessage({ id: 'Click to apply' })}
        </span>
      </div>
    );
  }, [formatMessage]);

  return (
    <Modal
      visible={visible}
      caption={getCaptions}
      onClose={toggleVisible}
      size={800}
      destroyOnClose
      className="dbo-historical-modal"
    >
      <GPT.HistoricalPrompt
        setPrompt={setPrompt}
        userPromptList={userPromptList}
        loading={loading}
        closeHistoricalPrompt={toggleVisible}
        getUserHistoryPrompt={getUserHistoryPrompt}
      />
    </Modal>
  );
};

export default PromptEditModal;
